import React, { useEffect, useState } from "react";
import { Box, Slider, Stack, TextField, Tooltip, Typography } from "@mui/material";
import typography from "../cssUtils/typography";
import { ColorsConstants } from "../cssUtils/constants";

// A typed value may go past the slider's end, up to this many times its max.
export const TYPED_MAX_FACTOR = 10;
const LOG_STEPS = 1000;

const toSlider = (value, min, max, logScale) => {
  const clamped = Math.min(Math.max(value, min), max);
  if (!logScale) return clamped;
  return Math.round((Math.log(clamped / min) / Math.log(max / min)) * LOG_STEPS);
};

const fromSlider = (position, min, max, logScale, step) => {
  if (!logScale) return position;
  const raw = min * Math.pow(max / min, position / LOG_STEPS);
  // Round to a "nice" number for the magnitude, so dragging lands on 1,200 not 1,187.
  const magnitude = Math.pow(10, Math.max(Math.floor(Math.log10(raw)) - 1, 0));
  return Math.max(min, Math.round(raw / Math.max(magnitude, step)) * Math.max(magnitude, step));
};

export const validateNumber = (text, min, max) => {
  if (text === "" || text === null || text === undefined) return "Enter a number";
  const value = Number(text);
  if (!Number.isFinite(value)) return "Enter a number";
  if (value < min) return `Minimum is ${min.toLocaleString("en-IN")}`;
  if (value > max * TYPED_MAX_FACTOR) return `Maximum is ${(max * TYPED_MAX_FACTOR).toLocaleString("en-IN")}`;
  return "";
};

/**
 * A slider with a number field bound to the same value.
 *
 * The field commits on blur or Enter; typed values above the slider's max (up to 10x)
 * are accepted and the slider pins at its end. Invalid input shows an error and is not
 * passed to onChange. logScale suits wide ranges such as 1-1,000,000.
 */
const NumberSliderField = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  logScale = false,
  unit = "",
  tooltip = "",
  disabled = false,
  helperText = "",
  testId,
}) => {
  const [text, setText] = useState(String(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setText(String(value));
    setError("");
  }, [value]);

  const commit = () => {
    const message = validateNumber(text, min, max);
    setError(message);
    if (message) return;
    const next = Number(text);
    if (next !== value) onChange(next);
  };

  const labelNode = (
    <Typography sx={{ ...typography.bodyLarge, color: disabled ? "text.disabled" : "inherit" }}>
      {label}
      {unit && <span style={{ opacity: 0.7 }}> ({unit})</span>}
    </Typography>
  );

  return (
    <Box sx={{ width: "100%" }} data-testid={testId}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        {tooltip ? <Tooltip title={tooltip}>{labelNode}</Tooltip> : labelNode}
        <TextField
          size="small"
          type="number"
          value={text}
          disabled={disabled}
          error={Boolean(error)}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          inputProps={{ min, step, "aria-label": label }}
          sx={{ width: 150, "& input": { textAlign: "right" } }}
        />
      </Stack>
      <Slider
        value={toSlider(Number(value) || min, min, max, logScale)}
        min={logScale ? 0 : min}
        max={logScale ? LOG_STEPS : max}
        step={logScale ? 1 : step}
        disabled={disabled}
        onChange={(e, position) => onChange(fromSlider(position, min, max, logScale, step))}
        valueLabelDisplay="auto"
        valueLabelFormat={() => Number(value).toLocaleString("en-IN")}
        aria-label={`${label} slider`}
        sx={{
          "& .MuiSlider-thumb": { color: ColorsConstants.primaryBlue100 },
          "& .MuiSlider-track": { color: ColorsConstants.primaryBlue100 },
        }}
      />
      {(error || helperText) && (
        <Typography sx={{ ...typography.bodySmall, color: error ? "error.main" : "text.secondary", mt: -0.5 }}>
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default NumberSliderField;
